import type { DrawPoint } from "./types";

/**
 * Tracks the last known viewport position of the primary pointer, because the
 * space bar carries no coordinates but a synthetic pen-down needs some.
 *
 * Module scope, not a store: this updates on every pointer move and must not
 * cause a React re-render. One mutable slot rather than an object per move —
 * `pointermove` fires at 60-120 Hz for the whole session. `hasPoint` stands in
 * for `null` so "no position yet" is still representable.
 */
const lastPoint = { x: 0, y: 0 };
let hasPoint = false;
let physicalPointerDown = false;
let listening = false;

function setLastPoint(e: PointerEvent) {
  lastPoint.x = e.clientX;
  lastPoint.y = e.clientY;
  hasPoint = true;
}

function onPointerMove(e: PointerEvent) {
  // Ignore touch: a touch "move" only exists while pressed, so it would leave
  // a stale coordinate behind that no longer reflects where the user is.
  if (e.pointerType === "touch") return;
  setLastPoint(e);
}

function onPointerDown(e: PointerEvent) {
  if (e.pointerType === "touch") return;
  physicalPointerDown = true;
  setLastPoint(e);
}

function onPointerUp(e: PointerEvent) {
  if (e.pointerType === "touch") return;
  physicalPointerDown = false;
}

/**
 * A pointerup is not guaranteed — dragging out of the window, an OS
 * interruption or alt-tab while pressed can all swallow it, and the flag left
 * set would refuse every engage for the life of the page. `drawLock` owns the
 * interrupt events and calls this from the same handler.
 */
export function interruptPointerTracking(): void {
  physicalPointerDown = false;
}

export function startPointerTracking(): void {
  if (listening) return;
  listening = true;
  // Capture phase so we still see the position even if something downstream
  // stops propagation.
  globalThis.addEventListener("pointermove", onPointerMove, { capture: true });
  globalThis.addEventListener("pointerdown", onPointerDown, { capture: true });
  globalThis.addEventListener("pointerup", onPointerUp, { capture: true });
  globalThis.addEventListener("pointercancel", onPointerUp, { capture: true });
}

export function stopPointerTracking(): void {
  if (!listening) return;
  listening = false;
  globalThis.removeEventListener("pointermove", onPointerMove, {
    capture: true,
  });
  globalThis.removeEventListener("pointerdown", onPointerDown, {
    capture: true,
  });
  globalThis.removeEventListener("pointerup", onPointerUp, { capture: true });
  globalThis.removeEventListener("pointercancel", onPointerUp, {
    capture: true,
  });
  hasPoint = false;
  physicalPointerDown = false;
}

/**
 * A COPY of the last known position, or null if the pointer has never moved.
 * Copied because the internal slot is mutated on every move — handing it out
 * would let a caller see coordinates change underneath it mid-engage.
 */
export function getLastPointerPoint(): DrawPoint | null {
  if (!hasPoint) return null;
  return { x: lastPoint.x, y: lastPoint.y };
}

/**
 * True while a real mouse/pen button is held. The lock refuses to engage then,
 * so a synthetic stroke can never interleave with a physical one.
 */
export function isPhysicalPointerDown(): boolean {
  return physicalPointerDown;
}

/** Test seam. */
export function __resetPointerTrackerForTests(): void {
  hasPoint = false;
  physicalPointerDown = false;
}
